/**
 * Story 057 — Edit-turbine modal (`/turbines?turbine=<uuid>`).
 *
 * R58-CC-02b: the read-only / role-gating pattern is delegated to the
 * shared `<ReadOnlyModal>` primitive in `@aeriosiq/ui` + the
 * `useCanEditResource` hook. This file no longer hand-rolls:
 *   - the inline read-only banner JSX (now the primitive's banner with
 *     `role="status"` + `aria-live="polite"`);
 *   - the per-input `slotProps.htmlInput.readOnly` plumbing on editable
 *     fields (now the primitive's wrapping `<fieldset disabled>` — the
 *     identity + audit fields keep their `readOnly: true` flag because
 *     they are *permanently* read-only regardless of role);
 *   - the local `MUTATE_ROLES` set (now centralised in
 *     `useCanEditResource`).
 *
 * Behaviour preserved verbatim from the pre-CC-02b build:
 *   - Loads `GET /turbines/{id}` on open.
 *   - Editable fields gated on the same mutate-role set
 *     (`tenant_admin | project_manager | back_office`) — sourced from
 *     `useCanEditResource(roles, 'turbine')`.
 *   - **Park (siteId) is editable for `tenant_admin` only** —
 *     Christian's Q4 lock 2026-05-13. For project_manager + back_office
 *     the field is `aria-disabled` with the explanatory tooltip; the BE
 *     also enforces the gate (spec 060).
 *   - Re-park triggers a confirmation dialog before PATCH (AC12).
 *   - Inactive turbines are read-only regardless of role; the
 *     primitive's `bannerOverride` carries the "Reactivate to edit." copy
 *     and `showActionsInReadOnly` keeps the Reactivate-turbine button
 *     reachable.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { ReadOnlyModal, useCanEditResourceFor } from "@aeriosiq/ui";
import { useHasRole } from "@aeriosiq/auth-client";
import { useToast } from "../../account/ToastContext.js";
import { ConfirmDialog } from "../../admin/shared/ConfirmDialog.js";
import { ExternalIdsField } from "../../sites/shared/ExternalIdsField.js";
import {
  useAssetModelsForTypeQuery,
  useDeactivateTurbineMutation,
  useReactivateTurbineMutation,
  useTurbineDetailQuery,
  useUpdateTurbineMutation,
  type TurbineDetail,
  type TurbineExternalId,
  type UpdateTurbineBody,
} from "../queries/index.js";
import {
  SiteAutocomplete,
  type SiteOption,
} from "../shared/SiteAutocomplete.js";
import { AssetModelAutocomplete } from "../shared/AssetModelAutocomplete.js";
import { CopyIcon } from "@aeriosiq/ui/icons";

interface EditTurbineFormValues {
  siteId: string | null;
  assetModelId: string | null;
  internalParkNumber: string;
  identifier: string;
  latitude: string;
  longitude: string;
  hubHeight: string;
  rotorDiameter: string;
  totalHeight: string;
  yearBuilt: string;
  commissionedAt: string;
  externalIds: TurbineExternalId[];
}

function detailToFormValues(d: TurbineDetail): EditTurbineFormValues {
  return {
    siteId: d.siteId,
    assetModelId: d.assetModelId,
    internalParkNumber: d.internalParkNumber ?? "",
    identifier: d.identifier ?? "",
    latitude: d.latitude === null ? "" : String(d.latitude),
    longitude: d.longitude === null ? "" : String(d.longitude),
    hubHeight: d.hubHeight === null ? "" : String(d.hubHeight),
    rotorDiameter: d.rotorDiameter === null ? "" : String(d.rotorDiameter),
    totalHeight: d.totalHeight === null ? "" : String(d.totalHeight),
    yearBuilt: d.yearBuilt === null ? "" : String(d.yearBuilt),
    commissionedAt: d.commissionedAt ?? "",
    externalIds: d.externalIds.map((e) => ({ provider: e.provider, id: e.id })),
  };
}

function toNumberOrNull(s: string): number | null {
  if (s.trim() === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}
function toIntOrNull(s: string): number | null {
  if (s.trim() === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}
function dropEmptyExternalIds(arr: TurbineExternalId[]): TurbineExternalId[] {
  return arr.filter(
    (r) => r.provider.trim().length > 0 && r.id.trim().length > 0,
  );
}

function buildUpdateBody(
  v: EditTurbineFormValues,
  original: TurbineDetail,
  canEditPark: boolean,
): UpdateTurbineBody {
  const body: UpdateTurbineBody = {
    internalParkNumber:
      v.internalParkNumber.trim().length > 0
        ? v.internalParkNumber.trim()
        : null,
    identifier: v.identifier.trim().length > 0 ? v.identifier.trim() : null,
    latitude: toNumberOrNull(v.latitude),
    longitude: toNumberOrNull(v.longitude),
    hubHeight: toNumberOrNull(v.hubHeight),
    rotorDiameter: toNumberOrNull(v.rotorDiameter),
    totalHeight: toNumberOrNull(v.totalHeight),
    yearBuilt: toIntOrNull(v.yearBuilt),
    commissionedAt:
      v.commissionedAt.trim().length > 0 ? v.commissionedAt.trim() : null,
    externalIds: dropEmptyExternalIds(v.externalIds),
  };
  if (v.assetModelId !== null && v.assetModelId !== original.assetModelId) {
    body.assetModelId = v.assetModelId;
  }
  // Only include `siteId` when the actor can re-park AND the value
  // actually changed. The BE 403's a non-tenant_admin attempt to PATCH
  // siteId — see spec 060 — but the FE belt-and-braces by simply not
  // sending it.
  if (canEditPark && v.siteId !== null && v.siteId !== original.siteId) {
    body.siteId = v.siteId;
  }
  return body;
}

function formatIso(iso: string | null | undefined): string {
  if (!iso) return "Unknown";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toISOString().slice(0, 19).replace("T", " ") + " UTC";
  } catch {
    return iso;
  }
}

export interface EditTurbineDialogProps {
  turbineId: string | null | undefined;
  onClose: () => void;
  /** All sites the actor can re-park to (active). */
  siteOptions: readonly SiteOption[];
}

export function EditTurbineDialog({
  turbineId,
  onClose,
  siteOptions,
}: EditTurbineDialogProps): ReactElement {
  const open = typeof turbineId === "string" && turbineId.length > 0;
  const { data: detail, isPending } = useTurbineDetailQuery(turbineId);

  // SB2-CC-01 — matrix-driven role gate.
  const canMutate = useCanEditResourceFor("turbine.update");
  // Park edit is tenant_admin-only (Christian's Q4 lock 2026-05-13).
  // Sourced via `useHasRole` (the one allowed role-identity check
  // outside the auth folder; the matrix doesn't yet model a
  // park-edit-specific permission key).
  const canEditPark = useHasRole("tenant_admin");

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm<EditTurbineFormValues>({
    defaultValues: detail
      ? detailToFormValues(detail)
      : {
          siteId: null,
          assetModelId: null,
          internalParkNumber: "",
          identifier: "",
          latitude: "",
          longitude: "",
          hubHeight: "",
          rotorDiameter: "",
          totalHeight: "",
          yearBuilt: "",
          commissionedAt: "",
          externalIds: [],
        },
    mode: "onChange",
  });

  useEffect(() => {
    if (detail) {
      reset(detailToFormValues(detail));
    }
  }, [detail, reset]);

  const update = useUpdateTurbineMutation();
  const deactivate = useDeactivateTurbineMutation();
  const reactivate = useReactivateTurbineMutation();
  const { toast } = useToast();

  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmRepark, setConfirmRepark] = useState<{
    fromSiteName: string;
    toSiteName: string;
    pendingValues: EditTurbineFormValues;
  } | null>(null);

  // Inactive turbines are read-only regardless of role; the primitive's
  // `bannerOverride` is used for that branch so the canonical "role
  // does not allow editing" copy is replaced with the inactive-turbine
  // explanation.
  const isInactive = detail?.isActive === false;
  const readOnly = !canMutate || isInactive;

  // Cascading models query — narrowed to the (read-only) Asset type
  // of the loaded turbine. The FE passes `assetTypeId` so the dropdown
  // groups options by scope just like the Add modal does.
  const cascadingModelsQuery = useAssetModelsForTypeQuery(
    detail?.assetTypeId ?? null,
  );
  const cascadingModels = cascadingModelsQuery.data?.items ?? [];

  const subtitle = useMemo(() => {
    if (!detail) return "";
    return `Turbine code ${String(detail.assetCode)} · ${detail.modelName} · ${detail.siteName}`;
  }, [detail]);

  function siteNameById(id: string | null): string {
    if (!id) return "—";
    return siteOptions.find((s) => s.id === id)?.siteName ?? "—";
  }

  async function performPatch(
    v: EditTurbineFormValues,
    original: TurbineDetail,
  ): Promise<void> {
    try {
      await update.mutateAsync({
        id: original.id,
        body: buildUpdateBody(v, original, canEditPark),
      });
      toast("Turbine updated.");
      onClose();
    } catch (err) {
      const errBody = (
        err as { body?: { code?: string; reason?: string; detail?: string } }
      ).body;
      if (errBody?.detail?.includes("forbidden_field")) {
        toast("Cannot set this field — it's managed by the system.", "error");
      } else if (errBody?.reason === "site_id_mutation_requires_tenant_admin") {
        toast("Only Tenant Admins can move a turbine between sites.", "error");
      } else {
        toast("Couldn't save turbine. Try again.", "error");
      }
    }
  }

  async function onSubmit(v: EditTurbineFormValues): Promise<void> {
    if (!detail) return;
    // If the actor is tenant_admin and they changed siteId, fire the
    // confirmation dialog first (Christian's Q4 lock + AC12).
    if (canEditPark && v.siteId !== null && v.siteId !== detail.siteId) {
      setConfirmRepark({
        fromSiteName: detail.siteName,
        toSiteName: siteNameById(v.siteId),
        pendingValues: v,
      });
      return;
    }
    await performPatch(v, detail);
  }

  async function confirmReparkAction(): Promise<void> {
    if (!detail || confirmRepark === null) return;
    const pending = confirmRepark.pendingValues;
    setConfirmRepark(null);
    await performPatch(pending, detail);
  }

  async function doDeactivate(): Promise<void> {
    if (!detail) return;
    try {
      await deactivate.mutateAsync(detail.id);
      const label = detail.internalParkNumber ?? `#${String(detail.assetCode)}`;
      toast(`Turbine ${label} deactivated.`);
      setConfirmDeactivate(false);
      onClose();
    } catch {
      toast("Couldn't deactivate turbine.", "error");
    }
  }

  async function doReactivate(): Promise<void> {
    if (!detail) return;
    try {
      await reactivate.mutateAsync(detail.id);
      const label = detail.internalParkNumber ?? `#${String(detail.assetCode)}`;
      toast(`Turbine ${label} reactivated.`);
      onClose();
    } catch {
      toast("Couldn't reactivate turbine.", "error");
    }
  }

  function copyTurbineId(): void {
    if (!detail) return;
    void navigator.clipboard.writeText(detail.id);
    toast("Turbine ID copied.");
  }

  // Title element — kept as a node so the existing
  // `id="edit-turbine-heading"` anchor is preserved for tests that
  // locate the dialog by `aria-labelledby`.
  const titleNode = (
    <>
      <Typography
        id="edit-turbine-heading"
        component="span"
        variant="h6"
        sx={{ display: "block" }}
      >
        Edit turbine
      </Typography>
      {detail ? (
        <Typography
          component="div"
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5 }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </>
  );

  // Banner override — when the modal is read-only because the turbine
  // is inactive (rather than because the role lacks edit permission) we
  // surface the more informative "Reactivate to edit." copy.
  const bannerOverride =
    readOnly && isInactive
      ? "This turbine is inactive. Reactivate to edit."
      : undefined;

  // Footer actions slot. When `readOnly` is true the primitive
  // suppresses this entirely (the Cancel + Save + Deactivate buttons
  // are not rendered in the DOM), satisfying R58-CC-02b AC3.
  //
  // Inactive + canMutate: a single primary "Reactivate turbine" button
  // (rendered via `showActionsInReadOnly`).
  // Otherwise the standard Cancel + Deactivate (left) + Save (right) row.
  const actionsNode = detail ? (
    !detail.isActive && canMutate ? (
      <>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void doReactivate();
          }}
          disabled={reactivate.isPending}
          startIcon={
            reactivate.isPending ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
        >
          Reactivate turbine
        </Button>
      </>
    ) : detail.isActive && canMutate ? (
      <>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          color="error"
          onClick={() => {
            setConfirmDeactivate(true);
          }}
        >
          Deactivate turbine
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          type="button"
          variant="contained"
          onClick={() => {
            void handleSubmit(onSubmit)();
          }}
          disabled={!isDirty || update.isPending}
          startIcon={
            update.isPending ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
        >
          Save
        </Button>
      </>
    ) : null
  ) : null;

  return (
    <>
      <ReadOnlyModal
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="edit-turbine-heading"
        readOnly={readOnly}
        entityNoun="turbine"
        {...(bannerOverride ? { bannerOverride } : {})}
        title={titleNode}
        actions={actionsNode}
        // When the modal is read-only specifically BECAUSE the turbine
        // is inactive (and the user CAN mutate), show the action row so
        // the Reactivate-turbine button is reachable.
        showActionsInReadOnly={readOnly && isInactive && canMutate}
      >
        {isPending ? (
          <Stack spacing={1.5}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rectangular" height={48} />
            ))}
          </Stack>
        ) : !detail ? (
          <Alert severity="error">Couldn&apos;t load this turbine.</Alert>
        ) : (
          <Stack spacing={2}>
            {/* IDENTITY — permanently read-only (system-managed). The
                `readOnly: true` on these inputs is independent of the
                role gate; the primitive's `<fieldset disabled>` wrap
                in read-only mode disables them further, which is
                visually identical for the user. */}
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Identity
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                label="Turbine ID"
                value={detail.id}
                variant="standard"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Turbine ID",
                    readOnly: true,
                    tabIndex: -1,
                  },
                }}
                fullWidth
              />
              <Tooltip title="Copy UUID">
                <IconButton
                  aria-label="Copy UUID to clipboard"
                  onClick={copyTurbineId}
                  size="small"
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <TextField
              label="Turbine Code"
              value={String(detail.assetCode)}
              variant="standard"
              slotProps={{
                htmlInput: {
                  "aria-label": "Turbine Code",
                  readOnly: true,
                  tabIndex: -1,
                },
              }}
              fullWidth
            />
            <TextField
              label="Asset type"
              value={detail.assetTypeName}
              variant="standard"
              slotProps={{
                htmlInput: {
                  "aria-label": "Asset type",
                  readOnly: true,
                  tabIndex: -1,
                },
              }}
              fullWidth
            />

            <Divider sx={{ my: 1 }} />
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Details
            </Typography>

            {/* Park — editable for tenant_admin only. For
                project_manager + back_office, render with
                `aria-disabled` + tooltip. When the modal is read-only
                (analyst / inactive), the primitive's <fieldset
                disabled> wrap takes precedence — no need for
                per-field plumbing. */}
            {canMutate && !canEditPark && detail.isActive ? (
              <Tooltip title="Only Tenant Admins can move a turbine between sites.">
                <Box>
                  <Controller
                    name="siteId"
                    control={control}
                    render={({ field }) => (
                      <SiteAutocomplete
                        value={field.value}
                        options={siteOptions}
                        onChange={field.onChange}
                        ariaDisabled
                      />
                    )}
                  />
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                      fontSize: 12,
                      color: "text.secondary",
                      mt: 0.5,
                    }}
                  >
                    Only Tenant Admins can move a turbine between sites.
                  </Box>
                </Box>
              </Tooltip>
            ) : (
              <Controller
                name="siteId"
                control={control}
                render={({ field }) => (
                  <SiteAutocomplete
                    value={field.value}
                    options={siteOptions}
                    onChange={field.onChange}
                  />
                )}
              />
            )}

            <Controller
              name="assetModelId"
              control={control}
              render={({ field }) => (
                <AssetModelAutocomplete
                  value={field.value}
                  options={cascadingModels}
                  loading={cascadingModelsQuery.isPending}
                  onChange={field.onChange}
                />
              )}
            />

            <TextField
              {...register("internalParkNumber")}
              label="Internal Park Name"
              slotProps={{
                htmlInput: {
                  "aria-label": "Internal Park Name",
                  maxLength: 50,
                },
              }}
              fullWidth
            />
            <TextField
              {...register("identifier")}
              label="Identifier"
              slotProps={{
                htmlInput: {
                  "aria-label": "Identifier",
                  maxLength: 100,
                },
              }}
              fullWidth
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                {...register("latitude")}
                label="Latitude"
                type="number"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Latitude",
                    step: "any",
                    min: -90,
                    max: 90,
                  },
                }}
                fullWidth
              />
              <TextField
                {...register("longitude")}
                label="Longitude"
                type="number"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Longitude",
                    step: "any",
                    min: -180,
                    max: 180,
                  },
                }}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                {...register("hubHeight")}
                label="Hub height (m)"
                type="number"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Hub height",
                    step: "any",
                    min: 0,
                  },
                }}
                fullWidth
              />
              <TextField
                {...register("rotorDiameter")}
                label="Rotor diameter (m)"
                type="number"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Rotor diameter",
                    step: "any",
                    min: 0,
                  },
                }}
                fullWidth
              />
              <TextField
                {...register("totalHeight")}
                label="Total height (m)"
                type="number"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Total height",
                    step: "any",
                    min: 0,
                  },
                }}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                {...register("yearBuilt")}
                label="Year built"
                type="number"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Year built",
                    step: 1,
                    min: 1980,
                    max: new Date().getFullYear() + 5,
                  },
                }}
                fullWidth
              />
              <TextField
                {...register("commissionedAt")}
                label="Commissioned at"
                type="date"
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: {
                    "aria-label": "Commissioned at",
                  },
                }}
                fullWidth
              />
            </Stack>

            <ExternalIdsField
              control={control}
              register={register}
              readOnly={readOnly}
            />

            <Divider sx={{ my: 1 }} />
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Audit
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Created at"
                value={formatIso(detail.createdAt)}
                variant="standard"
                slotProps={{
                  htmlInput: { readOnly: true, tabIndex: -1 },
                }}
                fullWidth
              />
              <AuditPersonField label="Created by" person={detail.createdBy} />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Updated at"
                value={formatIso(detail.updatedAt)}
                variant="standard"
                slotProps={{
                  htmlInput: { readOnly: true, tabIndex: -1 },
                }}
                fullWidth
              />
              <AuditPersonField label="Updated by" person={detail.updatedBy} />
            </Stack>
          </Stack>
        )}
      </ReadOnlyModal>

      <ConfirmDialog
        open={confirmDeactivate}
        title={`Deactivate turbine ${
          detail?.internalParkNumber ?? `#${String(detail?.assetCode ?? "")}`
        }?`}
        description={
          <Box>
            Deactivating hides this turbine from the default list but historical
            references (inspections, defects, findings) stay intact. You can
            reactivate it later from the inactive view.
          </Box>
        }
        confirmLabel="Deactivate turbine"
        danger
        pending={deactivate.isPending}
        onConfirm={() => {
          void doDeactivate();
        }}
        onCancel={() => {
          setConfirmDeactivate(false);
        }}
      />

      <ConfirmDialog
        open={confirmRepark !== null}
        title={`Confirm: moving turbine ${
          detail?.internalParkNumber ?? `#${String(detail?.assetCode ?? "")}`
        }`}
        description={
          confirmRepark === null ? null : (
            <Box>
              You are moving turbine{" "}
              <strong>
                {detail?.internalParkNumber ??
                  `#${String(detail?.assetCode ?? "")}`}
              </strong>{" "}
              from <strong>{confirmRepark.fromSiteName}</strong> to{" "}
              <strong>{confirmRepark.toSiteName}</strong>. Continue?
            </Box>
          )
        }
        confirmLabel="Confirm move"
        pending={update.isPending}
        onConfirm={() => {
          void confirmReparkAction();
        }}
        onCancel={() => {
          setConfirmRepark(null);
        }}
      />
    </>
  );
}

function AuditPersonField({
  label,
  person,
}: {
  label: string;
  person: { id: string; displayName: string } | null;
}): ReactElement {
  if (person === null) {
    return (
      <TextField
        label={label}
        value="Unknown"
        variant="standard"
        slotProps={{
          htmlInput: { readOnly: true, tabIndex: -1 },
          input: { sx: { fontStyle: "italic" } },
        }}
        fullWidth
      />
    );
  }
  return (
    <TextField
      label={label}
      value={person.displayName}
      variant="standard"
      slotProps={{
        htmlInput: { readOnly: true, tabIndex: -1 },
      }}
      fullWidth
    />
  );
}
